// src/templates/requests/ProviderSettings.jsx
import React, { useState, useEffect } from 'react';
import { Plus, Trash2, GripVertical, Settings2, Save } from 'lucide-react';
import { requestsService } from '../../services/requestsService';
import { FIELD_TYPES } from '../../components/shared/forms/FieldTypes';
import UniversalField from '../../components/shared/forms/UniversalField';

const DEFAULT_PROVIDER_FIELDS = [
  { id: 'businessName', name: 'businessName', type: FIELD_TYPES.TEXT, label: 'Business Name', required: true, order: 0 },
  { id: 'category', name: 'category', type: FIELD_TYPES.SELECT, label: 'Category', required: true, order: 1, options: [] },
  { id: 'description', name: 'description', type: FIELD_TYPES.TEXTAREA, label: 'Description', required: true, order: 2, rows: 4 },
  { id: 'email', name: 'email', type: FIELD_TYPES.EMAIL, label: 'Email', required: true, order: 3 },
  { id: 'phone', name: 'phone', type: FIELD_TYPES.PHONE, label: 'Phone', required: true, order: 4 },
  { id: 'website', name: 'website', type: FIELD_TYPES.URL, label: 'Website', required: false, order: 5 },
  { id: 'logo', name: 'logo', type: FIELD_TYPES.IMAGE, label: 'Logo', required: false, order: 6 }
];

const DEFAULT_CATEGORIES = [
  "Home Improvement & Repair",
  "Cleaning Services",
  "Plumbing & HVAC",
  "Electrical",
  "Moving & Storage",
  "Web & Graphic Design",
  "Digital Marketing",
  "Tutoring & Education",
  "Events & Catering",
  "Personal Training & Wellness",
  "Legal & Financial",
  "Other"
];

const ProviderSettings = ({ bookId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    fields: DEFAULT_PROVIDER_FIELDS,
    categories: DEFAULT_CATEGORIES,
    settings: {
      requireApproval: true,
      allowMultipleListings: false,
      enableVerifiedBadge: true,
      enableFeatured: true,
      maxListingsPerProvider: 1
    }
  });
  const [activeTab, setActiveTab] = useState('fields');
  const [editingField, setEditingField] = useState(null);
  const [newCategory, setNewCategory] = useState('');

  useEffect(() => {
    loadConfig();
  }, [bookId]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const data = await requestsService.getProviderConfig?.(bookId);
      if (data) {
        setConfig({
          fields: data.fields || DEFAULT_PROVIDER_FIELDS,
          categories: data.categories || DEFAULT_CATEGORIES,
          settings: data.settings || config.settings
        });
      }
    } catch (error) {
      console.error('Failed to load provider config:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await requestsService.updateProviderConfig?.(bookId, config);
    } catch (error) {
      console.error('Failed to save provider config:', error);
    } finally {
      setSaving(false);
    }
  };

  const addField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      name: '',
      type: FIELD_TYPES.TEXT,
      label: '',
      required: false,
      order: config.fields.length
    };
    setConfig(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
    setEditingField(newField.id);
  };

  const updateField = (fieldId, updates) => {
    setConfig(prev => ({
      ...prev,
      fields: prev.fields.map(f => f.id === fieldId ? { ...f, ...updates } : f)
    }));
  };

  const removeField = (fieldId) => {
    if (!window.confirm('Remove this field?')) return;
    setConfig(prev => ({
      ...prev,
      fields: prev.fields.filter(f => f.id !== fieldId)
    }));
  };

  const addCategory = () => {
    if (!newCategory.trim()) return;
    setConfig(prev => ({
      ...prev,
      categories: [...prev.categories, newCategory.trim()]
    }));
    setNewCategory('');
  };

  const removeCategory = (category) => {
    if (!window.confirm(`Remove "${category}" category?`)) return;
    setConfig(prev => ({
      ...prev,
      categories: prev.categories.filter(c => c !== category)
    }));
  };

  const updateSettings = (key, value) => {
    setConfig(prev => ({
      ...prev,
      settings: { ...prev.settings, [key]: value }
    }));
  };

  if (loading) {
    return <div className="py-8 text-center text-slate-500">Loading configuration...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Provider Settings</h2>
          <p className="text-sm text-slate-500 mt-1">Configure provider registration and listings</p>
        </div>
        <button
          onClick={saveConfig}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg hover:opacity-90 disabled:opacity-50"
        >
          <Save className="h-4 w-4" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 dark:border-slate-700">
        {['fields', 'categories', 'settings'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 font-medium capitalize transition-colors ${
              activeTab === tab
                ? 'border-b-2 border-slate-900 dark:border-white text-slate-900 dark:text-white'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Fields Tab */}
      {activeTab === 'fields' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Configure form fields shown during provider registration
            </p>
            <button
              onClick={addField}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700"
            >
              <Plus className="h-4 w-4" />
              Add Field
            </button>
          </div>

          <div className="space-y-3">
            {config.fields.sort((a, b) => a.order - b.order).map(field => (
              <div
                key={field.id}
                className="p-4 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
              >
                {editingField === field.id ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        placeholder="Field Name (e.g., businessName)"
                        value={field.name}
                        onChange={(e) => updateField(field.id, { name: e.target.value })}
                        className="px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 rounded-lg text-sm"
                      />
                      <input
                        type="text"
                        placeholder="Label (e.g., Business Name)"
                        value={field.label}
                        onChange={(e) => updateField(field.id, { label: e.target.value })}
                        className="px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 rounded-lg text-sm"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <select
                        value={field.type}
                        onChange={(e) => updateField(field.id, { type: e.target.value })}
                        className="px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 rounded-lg text-sm"
                      >
                        <option value={FIELD_TYPES.TEXT}>Text</option>
                        <option value={FIELD_TYPES.TEXTAREA}>Text Area</option>
                        <option value={FIELD_TYPES.EMAIL}>Email</option>
                        <option value={FIELD_TYPES.PHONE}>Phone</option>
                        <option value={FIELD_TYPES.URL}>URL</option>
                        <option value={FIELD_TYPES.SELECT}>Select</option>
                        <option value={FIELD_TYPES.IMAGE}>Image</option>
                        <option value={FIELD_TYPES.NUMBER}>Number</option>
                      </select>
                      <input
                        type="text"
                        placeholder="Placeholder text"
                        value={field.placeholder || ''}
                        onChange={(e) => updateField(field.id, { placeholder: e.target.value })}
                        className="px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 rounded-lg text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={field.required}
                          onChange={(e) => updateField(field.id, { required: e.target.checked })}
                          className="rounded"
                        />
                        Required
                      </label>
                      <button
                        onClick={() => setEditingField(null)}
                        className="ml-auto px-3 py-1 text-sm bg-slate-900 text-white rounded-lg"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <GripVertical className="h-4 w-4 text-slate-400" />
                      <div>
                        <div className="font-medium text-slate-900 dark:text-white">
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </div>
                        <div className="text-xs text-slate-500">
                          {field.name} • {field.type}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setEditingField(field.id)}
                        className="p-2 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
                      >
                        <Settings2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => removeField(field.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Manage provider categories for classification and filtering
          </p>

          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Add new category"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addCategory()}
              className="flex-1 px-4 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 rounded-lg"
            />
            <button
              onClick={addCategory}
              className="px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg hover:opacity-90"
            >
              Add
            </button>
          </div>

          <div className="space-y-2">
            {config.categories.map(category => (
              <div
                key={category}
                className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
              >
                <span className="text-slate-900 dark:text-white">{category}</span>
                <button
                  onClick={() => removeCategory(category)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          <div className="space-y-4">
            <label className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
              <div>
                <div className="font-medium text-slate-900 dark:text-white">Require Approval</div>
                <div className="text-sm text-slate-500">Providers must be approved before appearing</div>
              </div>
              <input
                type="checkbox"
                checked={config.settings.requireApproval}
                onChange={(e) => updateSettings('requireApproval', e.target.checked)}
                className="w-5 h-5 rounded"
              />
            </label>

            <label className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
              <div>
                <div className="font-medium text-slate-900 dark:text-white">Enable Verified Badge</div>
                <div className="text-sm text-slate-500">Show verified badge for approved providers</div>
              </div>
              <input
                type="checkbox"
                checked={config.settings.enableVerifiedBadge}
                onChange={(e) => updateSettings('enableVerifiedBadge', e.target.checked)}
                className="w-5 h-5 rounded"
              />
            </label>

            <label className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
              <div>
                <div className="font-medium text-slate-900 dark:text-white">Enable Featured</div>
                <div className="text-sm text-slate-500">Allow featuring providers at the top</div>
              </div>
              <input
                type="checkbox"
                checked={config.settings.enableFeatured}
                onChange={(e) => updateSettings('enableFeatured', e.target.checked)}
                className="w-5 h-5 rounded"
              />
            </label>

            <label className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
              <div>
                <div className="font-medium text-slate-900 dark:text-white">Allow Multiple Listings</div>
                <div className="text-sm text-slate-500">Let providers create multiple profiles</div>
              </div>
              <input
                type="checkbox"
                checked={config.settings.allowMultipleListings}
                onChange={(e) => updateSettings('allowMultipleListings', e.target.checked)}
                className="w-5 h-5 rounded"
              />
            </label>

            <div className="p-4 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
              <div className="font-medium text-slate-900 dark:text-white mb-2">Max Listings Per Provider</div>
              <div className="text-sm text-slate-500 mb-3">Maximum number of listings per provider (0 = unlimited)</div>
              <input
                type="number"
                min="0"
                value={config.settings.maxListingsPerProvider}
                onChange={(e) => updateSettings('maxListingsPerProvider', parseInt(e.target.value) || 0)}
                className="w-32 px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderSettings;